import { FC } from 'react'

import { useTheme } from '@mui/material'

import { categoryColor } from '../../helpers/diagnosisStyle'
import { ComparisonCard, ComparisonCategory, ComparisonDiagnosisCard } from '../ComparisonDiagnosisCard/ComparisonDiagnosisCard'
import { DiagnosisColumnShell } from '../DiagnosisPrimitives/DiagnosisColumnShell'

interface ComparisonDiagnosisColumnProps {
  category: ComparisonCategory
  cards: ComparisonCard[]
  emptyText: string
  sourceName: string
  targetName: string
  /** Source game app icon, shown at each card's gap bar source marker. */
  sourceIcon?: string
  /** Target game/genre app icon, shown at each card's gap bar delivered marker. */
  targetIcon?: string
  /** Invoked with the motivation name when a card is clicked. */
  onCardClick: (motivationId: string) => void
}

/**
 * The stack of comparison diagnosis cards (or a dashed empty-state). The
 * category banner header was dropped in favour of the lead-in Diagnosis
 * summary, which already labels and counts the diverging motivations; the
 * `category` still drives the per-card accent via {@link categoryColor}.
 */
export const ComparisonDiagnosisColumn: FC<ComparisonDiagnosisColumnProps> = ({
  category,
  cards,
  emptyText,
  sourceName,
  targetName,
  sourceIcon,
  targetIcon,
  onCardClick,
}) => {
  const theme = useTheme()
  const accent = categoryColor(theme, category)

  return (
    <DiagnosisColumnShell
      cards={cards}
      emptyText={emptyText}
      renderCard={(card, index) => (
        <ComparisonDiagnosisCard
          key={card.motivation.id}
          card={card}
          accent={accent}
          sourceName={sourceName}
          targetName={targetName}
          sourceIcon={sourceIcon}
          targetIcon={targetIcon}
          // Cards arrive pre-sorted by divergence (widest first), so the first
          // one is reliably the biggest gap to headline.
          isTopOfBucket={index === 0}
          onClick={() => onCardClick(card.motivation.id)}
        />
      )}
    />
  )
}
